/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The context roots a mapping may address, declared once for both readers** (`XS2a`, `#1213`).
 *
 * Before this file the vocabulary was two hand-kept lists that nothing compared, and they
 * disagreed **in both directions, three roots each way** — measured 2026-09-01 by running both
 * sides:
 *
 * | | console | engine |
 * | --- | --- | --- |
 * | `user.*` `content.*` `context.*` | **affirmed** — `_ref` aliases, followed by `isKnownContextPath` | `undefined` — not context keys, so the bare-path branch read `pod.user`, which `PodDto` has not |
 * | `params.*` `headers.*` `response.*` | offered nowhere, reported **unknown** | resolves |
 *
 * The first row is the worse one: the console *affirmed* a path that sends nothing, so the surface
 * read as proof (`#1002`).
 *
 * ## What this declares, and what it deliberately does not
 *
 * **It does not become the thing the engine branches on.** `resolveMacroPath` tests
 * `parts[0] in ctx`, deriving the roots from the context object it is handed. That is correct and
 * **open** — a runtime layer may attach extra signals, which `MacroResolveContext`'s index
 * signature exists for — and replacing it with a closed list would trade one defect for a smaller
 * vocabulary. So this declares the roots an **author** may address and every spelling that reaches
 * them; the engine keeps its `in ctx`, and {@link CONTEXT_ROOT_WRITERS} is what a spec checks the
 * two against.
 *
 * ## The alias is an input spelling, never a stored form
 *
 * Ratified by Todd on `#1213`: *"if I type `deviceType` the autocomplete offers
 * `session.user.deviceType`. If I paste in `user.deviceType` it should resolve to
 * `session.user.deviceType`."* So an alias is canonicalised **on the way in** — see
 * {@link canonicalizeContextPath} — and what is stored, previewed and resolved is always the
 * canonical path. Neither of the two obvious fixes was right: teaching the engine the aliases puts
 * them in storage to be resolved forever, and dropping them removes a helper an operator genuinely
 * wants.
 *
 * **Ambiguity asks rather than guesses.** No alias here is ambiguous — each names exactly one
 * canonical prefix — but the rule belongs with the vocabulary, because the ambiguous case arrives
 * with the *bare* spellings the picker matches, and resolving one silently by object-key order is
 * `#1003` (`position → device.os`, `content_language → userAgent`).
 */

/** One root an author may address, and what it holds. */
export interface ContextRoot {
  /** The canonical first segment. */
  readonly name: string;
  /** What an author will find under it. Operator-facing; it reaches the path picker. */
  readonly description: string;
  /**
   * The shorthand root, resolved by the engine ahead of every named key.
   *
   * Exactly one root carries this: `session` reads `context.session` with the
   * `pod.sessionMetadata` fallback the drivers rely on (one name since `#1524`).
   */
  readonly shorthand?: true;
  /**
   * **The root holds arbitrary keys, so its children cannot be enumerated.**
   *
   * A query-parameter map, a header map and a response body are whatever the caller sent or the
   * exchange returned. A closed root (`session`, `pod`, `demandSource`) is a typed document whose
   * children a schema can list; an open one is not, and treating the two alike is what kept these
   * three offered nowhere — a picker that can only offer what it can enumerate silently drops the
   * roots it cannot.
   *
   * So an open root is **offered as a root**, and any path beneath it is valid: the operator knows
   * their own parameter names and we do not. That is `X15` — restrict what is impossible, and *we
   * cannot list your keys* is not impossible, it is merely unknown to us.
   */
  readonly open?: true;
}

/** A spelling that means a root's path, canonicalised before anything stores it. */
export interface ContextRootAlias {
  /** What an operator may type or paste — a whole first segment, never a substring. */
  readonly spelling: string;
  /** The prefix it means. */
  readonly canonical: string;
  /** Why the alias exists, so removing it is a decision rather than a tidy. */
  readonly reason: string;
}

/**
 * Every root a mapping may address.
 *
 * **Adding one is two obligations, not one** — declare it here *and* make a context builder write
 * it, or it joins `#977`'s family: addressable, offered, and resolving to nothing forever.
 * `context-roots.spec.ts` fails on a root no builder claims.
 */
export const CONTEXT_ROOTS: readonly ContextRoot[] = [
  {
    name: 'session',
    description: 'Session metadata — the viewer, the content, the app and the request context',
    shorthand: true,
  },
  { name: 'pod', description: 'The pod being built, and the fields flattened onto it for macro access' },
  { name: 'demandSource', description: 'The demand source this request is being built for' },
  { name: 'params', description: 'The inbound query-parameter map — any parameter the caller sent', open: true },
  { name: 'headers', description: 'The inbound request header map — any header the caller sent', open: true },
  {
    name: 'response',
    description: "The demand source's response, during the response phase",
    open: true,
  },
  {
    name: 'round',
    description:
      'The round being planned — `round.last` is what the previous round did (`RT12`). Read by plan construction only, never handed to a source',
  },
] as const;

/**
 * The spellings that are not roots.
 *
 * All three point into `session`, and they exist because `user.deviceType` is what a person types.
 * They were previously `_ref` nodes in the console's schema, followed by `isKnownContextPath` and
 * understood by nothing else — so the console affirmed them and the engine returned `undefined`.
 */
export const CONTEXT_ROOT_ALIASES: readonly ContextRootAlias[] = [
  {
    spelling: 'user',
    canonical: 'session.user',
    reason: 'the viewer is the most-addressed subtree, and `user.` is what an operator types',
  },
  {
    spelling: 'content',
    canonical: 'session.content',
    reason: 'symmetric with `user`; pasted vendor documentation frequently spells it this way',
  },
  { spelling: 'context', canonical: 'session.context', reason: 'symmetric with `user`; the player/page subtree' },
] as const;

/**
 * Where a resolve context is **constructed**, and which roots each one sets.
 *
 * **This is about reading, and only reading.** A root here is one an authored path may *resolve
 * against* on that surface. It says nothing about what a mapping may **write to** — response
 * mapping's *target* is the ad, the pod, and (the case `#919` defined) the session, and none of
 * that is this map's subject. Reading and writing are different questions and the second is
 * `#1215`: extraction's output lands on `extractedPixelData`, which **has no reader**, and nothing
 * carries it into the session at all.
 *
 * *(Named `CONTEXT_ROOT_WRITERS` for a few hours on 2026-09-01, which read as "writes to the
 * session" when it meant "sets a key in the read context" — `#1045` inside the declaration written
 * to end an ambiguity. Renamed after Todd asked which one it meant.)*
 *
 * The entries record keys **set**, not roots **addressable**, and the difference is load-bearing:
 * `session` resolves wherever `pod` is set, because `resolveMacroPath` falls back to
 * `pod.sessionMetadata`. Recording *addressable* would make this a restatement of
 * the resolver rather than a claim about the builders — and `context-roots.spec.ts` reads each
 * real construction site, which is only possible against the narrower claim.
 *
 * It is a record of *what is*, not a policy about what each surface **may** address — that is
 * `X14`/`XS2b`, and a separate slice.
 */
export const RESOLVE_CONTEXT_BUILDERS: Readonly<Record<string, ContextBuilder>> = {
  /**
   * **The one every mapping path resolves against** (`#1000`) — headers, parameter expressions and
   * `mappingSource` all root here, and since 2026-09-01 so does response extraction.
   */
  macroContext: {
    file: 'libs/drivers/demand-source-driver.ts',
    marker: 'protected buildMacroContext(',
    opensAt: 'return {',
    sets: ['pod', 'demandSource', 'session', 'params', 'headers'],
    fanOut: null,
    note: 'outbound — no response exists yet',
  },
  /**
   * **Plan construction** (`RT4`, `R-d`, `#1535`): opt-engine resolving a round override whose inputs a
   * driver is not handed, before the round is enqueued, and shipping the answer as a literal. It sees
   * the whole session and pod, not a driver's projection of them, plus `round`.
   */
  planConstruction: {
    file: 'apps/opt-engine/src/plan-construction.ts',
    marker: 'export function buildPlanContext(',
    opensAt: 'return {',
    sets: ['session', 'pod', 'demandSource', 'round'],
    fanOut: null,
    note: 'no request exists yet, so no `params` or `headers`; once per source a round may call',
  },
  /**
   * **A round condition** (`RQ6`, slice 5): opt-engine evaluating a condition's trigger before each
   * queued round, in plan construction. The session, the pod and `round` (its number, and `round.last`
   * with no source bound), because a condition decides the whole round rather than one source's request.
   */
  roundCondition: {
    file: 'apps/opt-engine/src/plan-construction.ts',
    marker: 'export function buildConditionContext(',
    opensAt: 'return {',
    sets: ['session', 'pod', 'round'],
    fanOut: null,
    note: 'once per round, before it is enqueued; no source is bound, so no `demandSource`',
  },
  /*
   * **No decorator is a builder** (`RT8`, `R-c`, 2026-09-30). `requestDecorator` (the macro-param
   * decorator) and `contextSignal` were declared here, and no production path runs a decorator: the
   * pipeline was removed with `#869`, and `addDecorator` is called only from specs
   * (`RESOLUTION_TIER_DESIGN.md` §3.8). A builder declared here is one every consumer of this map
   * believes runs. `contextSignal` was added under `#1524` the same day, before §3.8 was read again.
   */
  ortbBodyTemplate: {
    file: 'libs/drivers/openrtb.driver.ts',
    marker: 'const ctx: MacroResolveContext = {',
    opensAt: 'const ctx: MacroResolveContext = {',
    sets: ['pod', 'demandSource', 'session', 'params', 'headers'],
    fanOut: null,
    note: 'outbound — no response exists yet',
  },
  /**
   * **The one this map did not know existed** (`#1236`, `XS3` slice 6).
   *
   * `applyResponseMapping` has constructed a context since `#866` and was recorded here by nothing,
   * which the spec above could not catch: *every declared root is set by at least one builder* is an
   * **inclusion** test, and a missing builder satisfies it perfectly. *A gate that can be satisfied
   * by doing less*, in the file whose whole job is saying what resolves where.
   *
   * The cost was not tidiness. The console reads this to answer *what can I read here*, so response
   * mapping refused the expression library on the stated grounds that *"library expressions read the
   * session … none of them would resolve here"* — a refusal, which is the sentence least likely to
   * be checked, and false.
   */
  responseMapping: {
    file: 'libs/drivers/response-mapping.resolver.ts',
    marker: 'export function applyResponseMapping(',
    opensAt: 'const context = {',
    sets: ['pod', 'demandSource', 'session', 'response'],
    /**
     * **The one fan-out in the system** (`X56a`, `#1296`). `applyResponseMapping` is called from
     * `extractAdFromBid` — inside ORTB's `seatbid[].bid[]` loop and VAST's `splitVastAds` loop — so
     * the document is applied once per returned ad and `response` is that ad's own parsed bid or
     * markup. The other three roots are built from the pod and are one object the whole run shares.
     */
    fanOut: {
      per: 'ad',
      varies: ['response'],
      reason:
        'Both drivers call this per ad: `openrtb.driver.ts` from `extractAdFromBid` inside the `seatbid[].bid[]` loop, `vast.driver.ts` from the `splitVastAds` loop. `pod`, `demandSource` and `session` are built from the pod once and do not change between ads.',
    },
    note: 'inbound — the request is over, so `params` and `headers` are genuinely out of scope here',
  },
} as const;

/**
 * **Every file that resolves an expression, and which context it resolves against** (`#1236`).
 *
 * {@link RESOLVE_CONTEXT_BUILDERS} is an **inclusion** test in both of its directions — *every
 * declared root is set by some builder*, *every builder names only declared roots*. Neither can see
 * a **construction site nobody declared**, and one had been there since `#866`: `applyResponseMapping`
 * built a context, resolved against it, and appeared in this file nowhere. *A gate that can be
 * satisfied by doing less.*
 *
 * So this closes the other direction: the spec **scans for every resolve call** and fails on one this
 * list does not account for, and on a listed file whose calls have gone. It is a file-level count
 * rather than a per-call declaration because the count is what changes when somebody adds a site —
 * a new call in a listed file fails just as loudly as a new file.
 *
 * **It found one immediately**, which is the argument for having it: `vast.driver.ts` carried its own
 * copy of the pixel-extraction loop (retired by `#868`, so the file no longer resolves anything) with a hand-built `{ pod, demandSource, ...extracted }` context,
 * so `params.*` and `headers.*` resolved to nothing on the VAST path alone. `#1000` had fixed the
 * identical defect in `BaseDriver`'s copy on 2026-09-01 and this copy was not found, because nothing
 * enumerated the sites.
 *
 * `contexts` names the builders a file's calls resolve against. A file that **derives** one — spreads
 * a builder's result and adds to it — still names the builder, because what an author may address is
 * the builder's set plus whatever the spread adds, and the addition is local to that call.
 */
export const RESOLVE_CONTEXT_CONSUMERS: Readonly<Record<string, ResolveContextConsumer>> = {
  'libs/drivers/demand-source-driver.ts': {
    calls: 5,
    contexts: ['macroContext'],
    note: "headers, parameter expressions, a bare `mappingSource`, and pixel extraction — all through `buildMacroContext`. Pixel extraction is the only copy since `#868` retired the VAST driver's. The fifth (`#1458`) re-reads a header's own path, against the header's own context, only to report an object it could not send",
  },
  'libs/drivers/openrtb.driver.ts': { calls: 1, contexts: ['ortbBodyTemplate'], note: 'an `$expr` body leaf' },
  'libs/request-decorators/builtin/context-signal.decorator.ts': {
    calls: 1,
    contexts: [],
    note: "a context-signal mapping's `sourcePath` (`#1524`). It resolves through `resolveMacroPath` like every authored path, and **no production path runs it**: decorators are not a builder (`RT8`, §3.8)",
  },
  'apps/opt-engine/src/plan-construction.ts': {
    calls: 3,
    contexts: ['planConstruction', 'roundCondition'],
    note: "a round override whose inputs a driver is not handed, and a worked-out floor or timeout (slice 5), each resolved per source before the round is enqueued (`RT4`, `R-d`); a worked-out field value (`RQ15`, slice 6), the same builder over the session **before** the round's patch; a round condition is evaluated against `roundCondition`",
  },
  'libs/drivers/response-mapping.resolver.ts': {
    calls: 1,
    contexts: ['responseMapping'],
    note: 'inbound extraction — the site this list was written because nothing recorded',
  },
} as const;

/** One file that resolves expressions, and what it resolves them against. */
export interface ResolveContextConsumer {
  /** How many resolve calls the file makes. A new one is a decision, so it fails until declared. */
  readonly calls: number;
  /** Keys of {@link RESOLVE_CONTEXT_BUILDERS} whose contexts these calls use. */
  readonly contexts: readonly string[];
  /** What the calls are, so a count changing can be read rather than guessed at. */
  readonly note: string;
}

/**
 * **What a surface runs more than once for** — cardinality, `X56a` (`#1296`).
 *
 * A closed vocabulary because it is **ours**: how many times bidBrain applies a mapping is a
 * property of our own execution, not of a standard somebody else evolves. One member today, and a
 * second is a deliberate act that fails every site until it is answered — which is the point
 * (*close over a vocabulary we own*).
 *
 * `ad` is the only one, and it is real rather than anticipated: `applyResponseMapping` is called
 * from inside `extractAdFromBid` (ORTB, over `seatbid[].bid[]`) and from the `splitVastAds` loop
 * (VAST), so **the whole inbound document runs once per returned ad** and `response` is that one
 * ad. Nothing on the surface said so, which is the reported complaint: *"we need to help the user
 * understand this comes from every ad in the response."*
 */
export const FAN_OUTS = ['ad'] as const;
export type FanOut = (typeof FAN_OUTS)[number];

/**
 * **A surface that repeats, and what differs between the repetitions.**
 *
 * Two facts, because they are true at two levels and *a claim is stated once, at the level it is
 * true at* (`#1248`): the **surface** repeats, and only **some roots** differ across the
 * repetitions. On the response surface `response` is the ad being extracted and changes every time,
 * while `pod`, `demandSource` and `session` are one thing the whole run shares — so an operator
 * reading `demandSource.floorCpm` beside `response.price` is comparing one value against N, and
 * neither half of that is visible today.
 *
 * The sentences live here rather than in the surface that renders them, because the pixel editor
 * asks the identical question (`#527`: *"pixels will be associated with individual ads, not the
 * full response"*) and *where two surfaces make the same decision, they share the sentence, not
 * just the function* (`#1187`).
 */
export interface ContextFanOut {
  /** What one repetition **is**. Reaches the operator through {@link describeFanOut}. */
  readonly per: FanOut;
  /**
   * The roots whose value differs between repetitions. Every other root this builder sets is the
   * **same object** for all of them, and that is the marked case on the surface: once the surface
   * has said it runs per ad, *this one does not* is the fact that adds something.
   */
  readonly varies: readonly string[];
  /** Where the repetition comes from, named as the code that loops — so the claim can be checked. */
  readonly reason: string;
}

/**
 * *each ad in the response* — the noun phrase a surface builds its own sentence from.
 *
 * A fragment rather than a sentence, because the sentence around it is the surface's: response
 * mapping folds it into the line that also says what the driver extracts for free, and the pixel
 * surface will want its own. What must not differ between them is **what the repetition is**, which
 * is the part that comes from here.
 */
export function describeFanOut(fanOut: ContextFanOut): string {
  return `each ${fanOut.per} in the response`;
}

/** *The same for every ad* — the mark a root that does not vary carries. */
export function describeSharedAcrossFanOut(fanOut: ContextFanOut): string {
  return `the same for every ${fanOut.per}`;
}

/** One place a {@link https://example.invalid | MacroResolveContext} is constructed. */
export interface ContextBuilder {
  /** Repo-relative path, so a spec can read the real construction site rather than a copy of it. */
  readonly file: string;
  /** A literal that locates the construction in that file. */
  readonly marker: string;
  /** The text whose `{` opens the object literal — searched from {@link marker}. */
  readonly opensAt: string;
  /** The roots it sets as keys. */
  readonly sets: readonly string[];
  /**
   * **How many times this context is built per request, where it is more than once** (`X56a`).
   *
   * Required and nullable rather than optional, for `#1134`'s reason: an omitted `fanOut` would
   * read as *once per request* on a builder nobody had asked the question of, and a surface that
   * fans out silently is exactly the defect this declares away. `null` is the positive answer —
   * *built once, nothing repeats* — and every builder here answers it.
   */
  readonly fanOut: ContextFanOut | null;
  /** Why it writes fewer than the full set, where it does. */
  readonly note?: string;
}

/**
 * **Which tier updates each path** — grain's other half (`X20`, `XS2b`, `#1218`).
 *
 * `grain = f(inputs, ownership)`, and only the first argument was free. The input set is derived
 * from the expression itself (`X18`); **the ownership map is a fact about the services and nothing
 * can derive it**, so it is declared here and asserted against the real mutation sites by
 * `path-ownership.spec.ts` — the same discipline {@link RESOLVE_CONTEXT_BUILDERS} is held to.
 *
 * The rule it makes checkable is Todd's, 2026-09-02:
 *
 * > **_"Resolves at the highest level where the input is visible, and nothing changes below it."_**
 *
 * The second clause is what this declares. *Visible* is a **read** property and a name-set
 * comparison can reach it; *nothing changes below* is a **write** property and nothing could,
 * which is why resolving on visibility alone yields a value that was true when computed and false
 * when used — the staleness `X20` rejected caching to avoid, re-admitted through the definition.
 *
 * **It is deliberately not {@link RESOLVE_CONTEXT_BUILDERS}, and the difference is the whole
 * point.** That records which builder **sets** a root as a key — *construction*. This records
 * **mutation**, and the two come apart exactly where the trap is: opt-engine constructs a context
 * containing `pod`, and req-loop increments `pod.demandSourceCallCounts` inside it.
 *
 * ## It is one axis of two — this is **not** lifetime (`X22`)
 *
 * Todd, 2026-09-02: *"the object model defines 'lifetime' (what scope of the session the data
 * persists for) vs 'where it mutates' (what grain of the services actually change it)."* Tying grain
 * to where a value sits in the object model is tempting and wrong; the two are orthogonal, and this
 * declares only the second.
 *
 * `pod.demandSourceCallCounts` is the proof. Its lifetime is the **break** — carried across the
 * rounds of one pod, and not among {@link PATH_LIFETIME}'s round-scoped exceptions — and its writer
 * is **req-loop** (see its entry above), so it outlives the tier that changes it. An expression may
 * read it at opt-engine on the next round, and may not be *resolved* from it there, because req-loop
 * will change it below. One value, two axes, opposite answers.
 *
 * (This cited `pod.custom` until 2026-09-15, `#1310`. Its lifetime is declared right, but nothing
 * writes a break-scoped value into it — only `enrichPodForDriver`'s per-call flatten of the session
 * bag — so it proved a design rather than a behaviour.)
 *
 * The author's three questions and which axis answers each — *where can I read this* (both), *where
 * can I store this* (lifetime), *where can I change this* (**this map**) — are `X22`. Only the third
 * is answered today.
 */
export const RESOLUTION_TIERS = ['session-manager', 'opt-engine', 'req-loop'] as const;
export type ResolutionTier = (typeof RESOLUTION_TIERS)[number];

/**
 * Who updates a path, where *nobody at request time* is one of the answers.
 *
 * `config` and `inbound` are not tiers and must not be read as slower ones: a value nothing mutates
 * during a request is resolvable **anywhere**, which is the strongest grain there is rather than
 * the weakest.
 */
export type PathOwner = ResolutionTier | 'config' | 'inbound';

/**
 * **The rule is directional, and that is what makes it checkable** — *"nothing changes **below**
 * it"*, not *nothing else writes it*.
 *
 * Earliest first. A tier **above** the owner writing a path is not a violation: it happens before
 * anything could resolve, which is why the proxy establishing `session.strategyId` and opt-engine
 * later choosing one are both correct. A tier **below** the owner writing it is the whole defect —
 * the value was true when it was resolved and false when it was used.
 *
 * **This came out of the gate rejecting a first draft of {@link PATH_OWNERSHIP}.** Checking
 * *owner equality* reported eight disagreements on `session`, every one of them the proxy or
 * opt-engine **constructing** a session from inbound metadata. Syntax cannot tell construction from
 * mutation — `session.strategyId = x` is both, in two files — and the distinction that matters is
 * not *which* but *whether the object could already have been read*, which is exactly what tier
 * order encodes. Same split {@link RESOLVE_CONTEXT_BUILDERS} draws, arrived at from the other side.
 */
export const OWNER_ORDER: readonly PathOwner[] = ['config', 'inbound', 'session-manager', 'opt-engine', 'req-loop'];

/** How late an owner acts. A larger number is a later, hotter tier. */
export function ownerRank(owner: PathOwner): number {
  return OWNER_ORDER.indexOf(owner);
}

/**
 * Does a write by `writer` break the ownership of a path owned by `owner`?
 *
 * Only when the writer acts **after** the owner — *nothing changes below it*.
 */
export function violatesOwnership(owner: PathOwner, writer: PathOwner): boolean {
  return ownerRank(writer) > ownerRank(owner);
}

/** A path whose updates belong to a different owner than its root's default. */
export interface OwnershipException {
  /** The path **under** the root — `demandSourceCallCounts`, never `pod.demandSourceCallCounts`. */
  readonly path: string;
  readonly owner: PathOwner;
  /** Why this one differs. A bare exception is a decision nobody can check. */
  readonly reason: string;
}

export interface RootOwnership {
  /** Who updates everything under this root unless an exception says otherwise. */
  readonly owner: PathOwner;
  /** Why the default is what it is. */
  readonly reason: string;
  readonly exceptions: readonly OwnershipException[];
}

/**
 * **Declared at the root, with the exceptions on the entry** — this frame's own convention
 * (`#1190`), because an exclusion the mechanism cannot read is a comment.
 *
 * Measured 2026-09-02 by scanning every mutation site in `apps/` and `libs/`: **`pod` has 19
 * mutated fields and 13 of them are opt-engine's**, which is what makes a root-level default with
 * a short exception list the right shape rather than a per-path map.
 *
 * **`session-manager` is declared and owns nothing yet, deliberately.** It is post-1.0 and decided
 * (`#752`), and the corollary this repository ratified says a second case must be an *addition*
 * rather than a change: when session-grain resolution moves, an owner changes on a line here and
 * every consumer inherits it. Same reasoning as `/vmap`'s schedule declaring
 * `derived | upstream | declared` while emitting one.
 */
export const PATH_OWNERSHIP: Readonly<Record<string, RootOwnership>> = {
  pod: {
    owner: 'opt-engine',
    reason:
      "opt-engine owns the break and the round plan, and settles everything the plan settles. Measured: 13 of the 19 mutated fields are written only there. The round augmentation (`roundIndex`, `roundFloorCpm`, `roundTimeout`, the resolved overrides) is not a mutation of the pod: `driverPodForRound` builds it onto a copy per dispatch, on both paths (`R-a`, `#1516`), and where it resolves is `R-c`'s declaration.",
    exceptions: [
      {
        path: 'collectedBids',
        owner: 'req-loop',
        reason:
          'req-loop makes the call, so the bids it collects do not exist until after opt-engine authored the plan.',
      },
      {
        path: 'demandSourceCallCounts',
        owner: 'req-loop',
        reason:
          "Incremented at two sites inside req-loop as sources are dispatched. This is `X20`'s worked example: every input is *visible* when opt-engine authors the plan, so resolving on visibility alone would hand every source the count as of dispatch — silently, and wrongly, on the serving path.",
      },
    ],
  },
  session: {
    owner: 'opt-engine',
    reason:
      "Established by the proxy on the inbound request and updated by opt-engine thereafter — both legal, because the proxy acts **above** opt-engine and a higher tier writing is not a change *below*. **req-loop mutates nothing on it** — measured, and it is what makes every session-grain value resolvable above req-loop, which is `X19`'s central claim. Post-1.0 this owner becomes `session-manager` and nothing else moves.",
    exceptions: [],
  },
  demandSource: {
    owner: 'config',
    reason:
      "An authored artifact. **Nothing mutates it during a request** — the only writes measured are in `libs/adapters` (authoring-time transforms) and `VastDriver`'s in-place `params` shape normalisation at construction. So it is resolvable at any tier, which is the strongest grain rather than the weakest.",
    exceptions: [],
  },
  params: {
    owner: 'req-loop',
    reason:
      'The query parameters of the request being built, so they exist only once a driver is building one. On the immediate path the same applies with opt-engine as the caller — the owner is *whoever is making the call*, which is why this is round-grain wherever it happens.',
    exceptions: [],
  },
  headers: {
    owner: 'req-loop',
    reason: 'As `params` — the headers of the request in flight, built by the driver at send time.',
    exceptions: [],
  },
  round: {
    owner: 'opt-engine',
    reason:
      "`round.last` is written by opt-engine when a round completes (`completePod`, `RT12`), from the bids, the requests and the selection, all of which it holds there. Nothing below it writes the round's record.",
    exceptions: [],
  },
  response: {
    owner: 'req-loop',
    reason:
      'req-loop receives the response, so nothing below it can change what a mapping reads. **It is the one root whose owner the mutation test does not actually decide** — a response is produced once and never updated, so *owns updates to* is silent about it, and `req-loop` here records where it arrives rather than a tier that writes it. Corrected 2026-09-03 (`#1218`): this entry read *"req-loop holds the response, so extraction resolves there — this falls out of the rule rather than being an exception to it"*, and **holds is not owns updates to**. Every other req-loop entry in this map is a real mutation; this one was the reasoning\'s own counter-example, cited as its confirmation. Where extraction *runs* is settled by design rather than by this map: in the driver, which on the round path is req-loop, with pod-level resolution in opt-engine after selection (`RESOLUTION_TIER_DESIGN.md` `RT6`, 2026-09-30).',
    exceptions: [],
  },
};

/**
 * **Where each declared context resolves** — `X48`'s third fact, beside ownership and lifetime
 * (`RESOLUTION_TIER_DESIGN.md` `RT8`, `R-c`).
 *
 * `PATH_OWNERSHIP` says which tier **changes** a value, and `X20` says a mapping must not resolve at a
 * tier **earlier** than the owner of any input: req-loop changes it afterwards, so the value was true
 * when read and false when used. This says which tier **evaluates** each builder's context, so the two
 * can be compared. It is static on purpose (`X48`: *"changing where it resolves is a code change
 * anyway"*), and `resolution-sites.spec.ts` holds it to the code.
 *
 * **A driver's contexts are declared at `req-loop`.** The driver runs in whichever tier makes the call:
 * req-loop on a round, opt-engine on the immediate path, where nothing runs after it. `req-loop` is the
 * later of the two, so every input is final wherever it runs.
 *
 * `withholds` is how a site at an earlier tier stays honest: it names the paths it does not hand a
 * mapping, because a later tier still changes them. Plan construction (`RT4`) is the site that needs it.
 */
export interface ResolutionSite {
  readonly tier: ResolutionTier;
  /** Paths under the builder's roots this site does not give a mapping, because a later tier owns them. */
  readonly withholds: readonly string[];
  /**
   * **Paths under its roots this site binds nothing at** (slice 5): not owned elsewhere, simply not
   * there. `round.last.source` in a round condition, which decides the whole round, so no one source is
   * resolved for. Required, so a new site says it binds everything rather than being assumed to.
   */
  readonly unbound: readonly string[];
  readonly reason: string;
}

export const RESOLUTION_SITES: Readonly<Record<string, ResolutionSite>> = {
  macroContext: {
    tier: 'req-loop',
    unbound: [],
    withholds: [],
    reason: 'a driver building a request: headers, parameters and `mappingSource`, in the calling tier',
  },
  ortbBodyTemplate: {
    tier: 'req-loop',
    unbound: [],
    withholds: [],
    reason: 'a driver building the ORTB body, in the calling tier',
  },
  responseMapping: {
    tier: 'req-loop',
    unbound: [],
    withholds: [],
    reason: 'extraction, in the driver as the response arrives (`RT6`: the pod pass is a later site)',
  },
  planConstruction: {
    tier: 'opt-engine',
    unbound: [],
    withholds: ['pod.collectedBids', 'pod.demandSourceCallCounts'],
    reason:
      "a round override whose inputs the driver is not handed, resolved before the round is enqueued (`RT4`). It withholds req-loop's two counters, which do not exist yet when the plan is made (`X20`)",
  },
  roundCondition: {
    tier: 'opt-engine',
    withholds: ['pod.collectedBids', 'pod.demandSourceCallCounts'],
    unbound: ['round.last.source'],
    reason:
      "a round condition's trigger, evaluated in plan construction before the round is enqueued (`RQ6`, slice 5). It withholds req-loop's two counters as plan construction does: what the last round did is `round.last`, not a counter. It binds no `round.last.source`, because a condition decides the whole round",
  },
};

/**
 * **The paths a site would read below their grain**: every root the site's builder sets, and every
 * exception under it, whose owner acts after the site's tier and which the site does not withhold.
 * Empty is the only correct answer; `resolution-sites.spec.ts` asserts it for every site.
 */
export function readsBelowGrain(site: ResolutionSite, roots: readonly string[]): string[] {
  const below: string[] = [];
  for (const root of roots) {
    const ownership = PATH_OWNERSHIP[root];
    if (!ownership) continue;
    const owned = [
      { path: root, owner: ownership.owner },
      ...ownership.exceptions.map((e) => ({ path: `${root}.${e.path}`, owner: e.owner })),
    ];
    for (const { path, owner } of owned) {
      if (violatesOwnership(site.tier, owner) && !site.withholds.includes(path)) below.push(path);
    }
  }
  return below;
}

/**
 * **How long a value survives** — grain's other axis (`X22`, `XS2b`).
 *
 * Todd, 2026-09-02: *"the object model defines 'lifetime' (what scope of the session the data
 * persists for) vs 'where it mutates' (what grain of the services actually change it)."* Two
 * questions, and tying either to the other is the mistake {@link PATH_OWNERSHIP} was first framed
 * with.
 *
 * **They come apart on the very same fields.** `pod.demandSourceCallCounts` and `pod.collectedBids`
 * are both req-loop's to change, and `PodJobDto.createFromPayload` — the round boundary — carries the
 * first (*"accumulated across prior rounds"*) and resets the second to `[]`. Same owner, different
 * lifetime. No single number expresses that.
 *
 * Ordered **longest first**, which is what makes the comparison meaningful: a value may only be
 * stored somewhere that outlives whatever will read it, and *"storing something that gets discarded
 * between breaks or rounds should be known"* (Todd) is that comparison made visible to an author.
 */
export const VALUE_LIFETIMES = ['config', 'session', 'break', 'round', 'transient'] as const;
export type ValueLifetime = (typeof VALUE_LIFETIMES)[number];

/**
 * How long a lifetime lasts, longest first. `transient` is last because it survives nothing — it is
 * computed where it is used and never stored, which is `X21`'s *transient vs stored*.
 */
export function lifetimeRank(lifetime: ValueLifetime): number {
  return VALUE_LIFETIMES.indexOf(lifetime);
}

/**
 * Would storing a value of lifetime `stored` lose it before a reader at `readAt` sees it?
 *
 * The author-facing form of question 2. A break-scoped destination cannot hold something a later
 * break must read; a round-scoped one cannot hold something the next round needs.
 */
export function outlives(stored: ValueLifetime, readAt: ValueLifetime): boolean {
  return lifetimeRank(stored) <= lifetimeRank(readAt);
}

/**
 * **The sentence a lifetime is described with — one set, for both catalogs** (`X32` Q2, `#1248`).
 *
 * *Sources and destinations are two catalogs*, and a **lifetime is neither** — it is the vocabulary
 * both of them classify against. So the phrasing lives beside {@link VALUE_LIFETIMES} rather than
 * beside either catalogue: *shared logic is not shared words, and the words drift on their own*
 * (`#1187`). A destination surface asking *if I put it here, can I use it in later rounds or
 * breaks?* and a read surface asking how long a value has been around are asking about the same
 * fact, and must not come to say different things about it.
 *
 * It states the **consequence** rather than the mechanism (`#1045`): an operator does not need to
 * know what resets `collectedBids`, they need to know what survives the next request.
 */
const LIFETIME_PHRASE: Readonly<Record<ValueLifetime, string>> = {
  config: 'kept with the configuration — it outlives every session',
  session: 'kept for the whole session, across every ad break in it',
  break: 'kept for this ad break — every round in it, and rebuilt for the next break',
  round: 'kept for this round only — gone when the next request goes out',
  transient: 'not stored at all — it exists only where it is used',
};

/** How long a value of this lifetime survives, in the operator's terms. */
export function describeLifetime(lifetime: ValueLifetime): string {
  return LIFETIME_PHRASE[lifetime];
}

/**
 * **The same claim as a scope, for a place with one line to spend** (Todd, 2026-09-10).
 *
 * `LIFETIME_PHRASE` answers *how long does this last* in a sentence, which is right where an
 * operator is asking. Under a column heading it is three lines of standing prose above the rows,
 * and `#1248`'s own rule turns on it: a claim repeated where it is not being asked stops being read.
 *
 * It is a **short form, not a second claim** — the sentence is still rendered, in the accessible
 * tree and on the hover, because moving text off the surface for density deletes it outright for
 * anyone who cannot hover (`#1276`). Two audiences, one claim; never two claims.
 */
const LIFETIME_SCOPE: Readonly<Record<ValueLifetime, string>> = {
  config: 'every session',
  session: 'this session',
  break: 'each ad break',
  round: 'this round',
  transient: 'this use only',
};

/** The scope a value of this lifetime belongs to — {@link describeLifetime}'s short form. */
export function describeLifetimeScope(lifetime: ValueLifetime): string {
  return LIFETIME_SCOPE[lifetime];
}

/** A path whose lifetime differs from its root's default. */
export interface LifetimeException {
  readonly path: string;
  readonly lifetime: ValueLifetime;
  readonly reason: string;
}

export interface RootLifetime {
  readonly lifetime: ValueLifetime;
  readonly reason: string;
  readonly exceptions: readonly LifetimeException[];
}

/**
 * **Declared at the root with exceptions on the entry**, the same shape as {@link PATH_OWNERSHIP}
 * and for the same reason (`#1190`) — and the exceptions run **both ways**, which is the proof that
 * a root is a default rather than the answer. `pod.sessionMetadata` lives *longer* than its root;
 * the round fields live *shorter*.
 */
export const PATH_LIFETIME: Readonly<Record<string, RootLifetime>> = {
  pod: {
    lifetime: 'break',
    reason:
      'A pod is one ad break. Its state is rebuilt for the next break and read across the rounds within this one.',
    exceptions: [
      {
        path: 'sessionMetadata',
        lifetime: 'session',
        reason:
          'The session embedded on the pod. It outlives the break it is carried on, which is why a session-grain value may be read here without being break-scoped.',
      },
      {
        path: 'collectedBids',
        lifetime: 'round',
        reason:
          '`PodJobDto.createFromPayload` resets it to `[]` at every round boundary — what this round collected, and nothing earlier.',
      },
      {
        path: 'roundIndex',
        lifetime: 'round',
        reason: 'Identifies the round in flight, so it is replaced at each boundary by definition.',
      },
      {
        path: 'roundFloorCpm',
        lifetime: 'round',
        reason:
          'The floor this round asked for. The plan that chose it survives the break; the applied value does not.',
      },
      {
        path: 'roundTimeout',
        lifetime: 'round',
        reason: 'As `roundFloorCpm` — what this round applied rather than what the plan settled.',
      },
      {
        path: 'roundDemandSourceId',
        lifetime: 'round',
        reason: 'The source this round targeted, where it targeted one.',
      },
      {
        path: 'roundParamOverrides',
        lifetime: 'round',
        reason: 'The overrides applied for this round only — the *blind bids from round 5* case.',
      },
      {
        path: 'roundSuppressedParams',
        lifetime: 'round',
        reason: "As `roundParamOverrides` — this round's suppressions, not the break's.",
      },
      {
        path: 'roundHeaderOverrides',
        lifetime: 'round',
        reason: "As `roundParamOverrides`, for the source's headers (slice 4).",
      },
      {
        path: 'roundSuppressedHeaders',
        lifetime: 'round',
        reason: "As `roundSuppressedParams`, for the source's headers (slice 4).",
      },
      {
        path: 'roundBodyOverrides',
        lifetime: 'round',
        reason: "As `roundParamOverrides`, for the source's ORTB body (slice 4).",
      },
    ],
  },
  session: {
    lifetime: 'session',
    reason:
      'The session outlives every break in it. This is what makes a session-grain value worth resolving once and passing down (`X19`).',
    exceptions: [],
  },
  demandSource: {
    lifetime: 'config',
    reason:
      'An authored artifact in the config store. It outlives every session, which is why nothing during a request may be stored on it.',
    exceptions: [],
  },
  params: {
    lifetime: 'round',
    reason:
      'The query parameters of the request in flight. They exist while a driver is building one and are gone after it.',
    exceptions: [],
  },
  headers: {
    lifetime: 'round',
    reason: 'As `params` — the headers of the request being sent.',
    exceptions: [],
  },
  response: {
    lifetime: 'round',
    reason:
      "The response to this round's request. An extracted value has no destination that outlives the round that produced it: `PodDto.custom` declares the break-scoped home it should have, and no extraction writes it. That is `#1235`, which absorbed `#1215`.",
    exceptions: [],
  },
  round: {
    lifetime: 'round',
    reason:
      '`round.last` describes exactly one round and is replaced when the next one completes (`RT12`, `HF2`): it is read while planning the round after it, and never accumulates.',
    exceptions: [
      {
        path: 'last.source',
        lifetime: 'break',
        reason:
          "A source's record is the last round **this break** called it (`Q12`), so it carries across rounds that did not call the source and goes with the pod when the break is served.",
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Grain, resolved for one path (`X32`, `#1248`)
// ---------------------------------------------------------------------------

/**
 * **The lookup neither declaration had** (`#1248`, `XS3` slice 6).
 *
 * {@link PATH_OWNERSHIP} and {@link PATH_LIFETIME} are *root plus exceptions* maps, which is the
 * right shape to **declare** — `pod` has 19 mutated fields and 13 are opt-engine's — and the wrong
 * shape to **ask**. Measured 2026-09-04: both appeared in exactly two files, the declaration and its
 * own spec. **Nothing read them**, and one reason is that reading them meant re-implementing the
 * root/exception walk at every call site.
 *
 * So the walk lives here, once, beside what it walks. `X32`'s three questions are asked of a path by
 * a surface, and none of them is answerable without it.
 *
 * **Longest match wins**, so an exception on `a.b` beats one on `a` — the maps have no such pair
 * today and the rule costs nothing, but a first-match walk would make adding one a silent
 * behaviour change rather than an addition.
 */
function bestException<E extends { path: string }>(under: string, exceptions: readonly E[]): E | undefined {
  let best: E | undefined;
  for (const exception of exceptions) {
    const matches = under === exception.path || under.startsWith(`${exception.path}.`);
    if (matches && (!best || exception.path.length > best.path.length)) best = exception;
  }
  return best;
}

/** Split a path into its root and the part beneath it, canonicalising an alias spelling first. */
function splitPath(path: string): { root: string; under: string } {
  const [root, ...rest] = canonicalizeContextPath(path).split('.');
  return { root, under: rest.join('.') };
}

/** Who updates this path, and why — `undefined` for a path under no declared root. */
export function pathOwner(path: string): { owner: PathOwner; reason: string } | undefined {
  const { root, under } = splitPath(path);
  const entry = PATH_OWNERSHIP[root];
  if (!entry) return undefined;
  const exception = bestException(under, entry.exceptions);
  return exception
    ? { owner: exception.owner, reason: exception.reason }
    : { owner: entry.owner, reason: entry.reason };
}

/** How long this path's value lives, and why — `undefined` for a path under no declared root. */
export function pathLifetime(path: string): { lifetime: ValueLifetime; reason: string } | undefined {
  const { root, under } = splitPath(path);
  const entry = PATH_LIFETIME[root];
  if (!entry) return undefined;
  const exception = bestException(under, entry.exceptions);
  return exception
    ? { lifetime: exception.lifetime, reason: exception.reason }
    : { lifetime: entry.lifetime, reason: entry.reason };
}

/**
 * **`X32` question 1 — is this knowable on this surface?**
 *
 * Answered from {@link RESOLVE_CONTEXT_BUILDERS}, which records the roots each construction site
 * **sets** and is checked against the real source by `context-roots.spec.ts`. So *what the console
 * offers* and *what the runtime provides* are one declaration rather than two lists that drift.
 *
 * **It is a mark, never a filter** (`X32`): *"a path that cannot resolve at this phase is offered and
 * marked, not hidden — hiding it answers by silence, and the operator's next move is to type it by
 * hand and get nothing."* `#1232` hid `params.*` from the response surface on the strength of `#977`,
 * and the two are different things: `#977` is a name that resolves **nowhere, ever**, while this is a
 * name that resolves everywhere **except here**. One is a vocabulary defect; this is a property of
 * the phase, and the operator needs to be told which.
 *
 * `undefined` for a builder key this map does not carry — *nobody declared it* must not read as
 * *nothing resolves there*, the same reason `runtimeUndecided()` exists rather than returning empty.
 *
 * **It answers per path since `#1524`, for `session.` only.** A driver's surface sets the root, and
 * its session is a projection: `session.strategyId` or `session.cachedBids` resolve on the session
 * tier and to nothing here. The same X32 rule holds: offered and marked, not hidden.
 */
export function knowableAt(builderKey: string, path: string): boolean | undefined {
  const builder = RESOLVE_CONTEXT_BUILDERS[builderKey];
  if (!builder) return undefined;
  const [root, ...under] = canonicalizeContextPath(path).split('.');
  if (!builder.sets.includes(root)) return false;
  if (under.length === 0) return true;
  // An opt-engine site holds the whole session and pod, less what req-loop has not produced yet,
  // which the site declares it withholds (`RT4`, `R-d`).
  const site = RESOLUTION_SITES[builderKey];
  if (site?.tier === 'opt-engine') {
    const canonical = [root, ...under].join('.');
    return ![...site.withholds, ...site.unbound].some((w) => canonical === w || canonical.startsWith(`${w}.`));
  }
  // Every other builder runs over a driver's pod, so a `session` or `pod` path is knowable only where
  // the driver's projection carries it (`#1524`, `R-c`).
  if (root === 'session') return isCarriedToDriver(under.join('.'));
  if (root === 'pod') return isOnDriverPod(under.join('.'));
  return true;
}

/**
 * **What a driver's `session` carries** (`#1524`, `RESOLUTION_TIER_DESIGN.md` *`R-c`'s session audit*):
 * the projection `toDriverSession` builds on every path, so it is exactly what crosses the queue.
 * Anything else on the session is the session tier's own record and resolves to nothing on a driver's
 * surface, which is why {@link knowableAt} asks this.
 *
 * Pure data, because this module is aliased into the console. `round-session-context.spec.ts` checks
 * it against `fromRoundSessionContext` in both directions, so it cannot drift from what is carried.
 * An entry holding an object or a list (`custom`, `user.device`) carries everything beneath it.
 */
export const DRIVER_SESSION_PATHS: readonly string[] = [
  'adUnit',
  'allowDisabledSources',
  'content.contentChannel',
  'content.contentId',
  'content.contentLength',
  'content.contentNetwork',
  'content.contentRating',
  'content.contentTitle',
  'content.contentType',
  'content.contentUrl',
  'content.episodeNumber',
  'content.genres',
  'content.iabCategories',
  'content.keywords',
  'content.language',
  'content.playbackType',
  'content.seasonNumber',
  'content.seriesName',
  'context.appBundle',
  'context.appId',
  'context.appName',
  'context.appStoreUrl',
  'context.connectionType',
  'context.domain',
  'context.fullscreen',
  'context.muted',
  'context.pageUrl',
  'context.placement',
  'context.placementUnmapped',
  'context.playerHeight',
  'context.playerType',
  'context.playerWidth',
  'context.publisherId',
  'context.publisherName',
  'context.referrerUrl',
  'custom',
  'regs.gpp',
  'regs.gppSid',
  'requestHeaders',
  'sessionId',
  'sourceContext',
  'user.coppa',
  'user.device',
  'user.deviceName',
  'user.deviceType',
  'user.dnt',
  'user.gdpr',
  'user.gdprConsent',
  'user.geoLocation',
  'user.idfa',
  'user.ipAddress',
  'user.lmt',
  'user.secChUa',
  'user.secChUaMobile',
  'user.secChUaPlatform',
  'user.usPrivacy',
  'user.userAgent',
  'user.userId',
  'userExt.eids',
];

const DRIVER_SESSION_PATH_SET: ReadonlySet<string> = new Set(DRIVER_SESSION_PATHS);

/**
 * **What a driver's pod carries** (`R-c`, closing `RESOLUTION_TIER_DESIGN.md` §3.4). A round's driver
 * got the job on req-loop and opt-engine's full `PodDto` on the immediate path, so a round override
 * reading `pod.cachedBidRefs` resolved on one path and to nothing on the other. `driverPodForRound` now
 * keeps only these fields of the pod it is handed, on both paths: the job's fields, which is what
 * crosses the queue (`RT9`). `sessionMetadata` is `#1524`'s, projected separately.
 *
 * Pure data for the console; `round-for-source.spec.ts` checks it against `PodJobDto.createFromPayload`.
 */
export const DRIVER_POD_FIELDS: readonly string[] = [
  'cacheKey',
  'collectedBids',
  'demandSourceCallCounts',
  'demandSources',
  'fetchRound',
  'maxAds',
  'maxDuration',
  'maxFetchRounds',
  'optimizerStrategy',
  'podId',
  'podIndex',
  'reqLoopStartTime',
  'requestStrategyId',
  'roundPlan',
  'sessionId',
  'strategyFloorCpm',
  'strategyTimeoutMs',
  'strategyMaxConcurrent',
];

/**
 * **What `driverPodForRound` adds to a driver's pod**: the aliases `enrichPodForDriver` derives from the
 * session, and the round's augmentation. Declared so a surface can tell them from a pod field the
 * driver never gets; checked against the function's real output.
 */
export const DRIVER_POD_DERIVED: readonly string[] = [
  'adUnit',
  'appBundle',
  'appId',
  'appName',
  'appStoreUrl',
  'connectionType',
  'contentChannel',
  'contentDuration',
  'contentId',
  'contentNetwork',
  'contentRating',
  'contentTitle',
  'contentType',
  'contentUrl',
  'coppa',
  'custom',
  'deviceId',
  'deviceType',
  'dnt',
  'episodeNumber',
  'fullscreen',
  'gdpr',
  'gdprConsent',
  'genres',
  'idfa',
  'ip',
  'ipAddress',
  'language',
  'lmt',
  'muted',
  'pageUrl',
  'playbackType',
  'playerHeight',
  'playerType',
  'playerWidth',
  'publisherId',
  'publisherName',
  'roundBodyOverrides',
  'roundDemandSourceId',
  'roundFloorCpm',
  'roundHeaderOverrides',
  'roundIndex',
  'roundParamOverrides',
  'roundSuppressedHeaders',
  'roundSuppressedParams',
  'roundTimeout',
  'seasonNumber',
  'secChUa',
  'secChUaMobile',
  'secChUaPlatform',
  'seriesName',
  'ua',
  'usPrivacy',
  'userAgent',
  'userId',
];

const DRIVER_POD_KEY_SET: ReadonlySet<string> = new Set([
  ...DRIVER_POD_FIELDS,
  ...DRIVER_POD_DERIVED,
  'sessionMetadata',
]);

/** Is this path under `pod.` one a driver's pod carries? Given without the root. */
export function isOnDriverPod(podPath: string): boolean {
  const first = podPath.replace(/\[\d+\]/g, '').split('.')[0];
  return first === '' || DRIVER_POD_KEY_SET.has(first);
}

/**
 * Is this path under `session.` one a driver's session carries? The path is given without the root.
 * A list position (`genres[0]`, `genres.0`) is its list's, and anything beneath a carried object is
 * carried with it. The parents of carried paths (`user`, `content`) are carried too.
 */
export function isCarriedToDriver(sessionPath: string): boolean {
  const segments = sessionPath
    .replace(/\[\d+\]/g, '')
    .split('.')
    .filter((s) => s !== '' && !/^\d+$/.test(s));
  for (let i = segments.length; i > 0; i -= 1) {
    if (DRIVER_SESSION_PATH_SET.has(segments.slice(0, i).join('.'))) return true;
  }
  const prefix = segments.join('.') + '.';
  return DRIVER_SESSION_PATHS.some((p) => p.startsWith(prefix));
}

/**
 * **How many times a mapping on this surface runs** — `X56a` question 1 (`#1296`).
 *
 * `null` is a real answer (*once per request, nothing repeats*); `undefined` is a builder key this
 * map does not carry, and the two must not collapse for `knowableAt`'s reason — *nobody declared
 * this* is not *this happens once*.
 */
export function fanOutAt(builderKey: string): ContextFanOut | null | undefined {
  const builder = RESOLVE_CONTEXT_BUILDERS[builderKey];
  return builder ? builder.fanOut : undefined;
}

/**
 * **Is this path one value for the whole run, on a surface that repeats?** — `X56a` question 2.
 *
 * The marked case, and the only one: once the surface has said it runs once per ad, *this one is
 * the same for every ad* is what contradicts the assumption it just created. `false` is the
 * unmarked majority and `undefined` means there is no fan-out here to be shared across — three
 * answers, because *there is no repetition* and *this varies with it* are different sentences and
 * a boolean would make the first read as the second.
 */
export function sharedAcrossFanOut(builderKey: string, path: string): boolean | undefined {
  const fanOut = fanOutAt(builderKey);
  if (!fanOut) return undefined;
  // A root this builder does not set is one we cannot speak for, so it gets no mark rather than
  // the default one: `!varies.includes(root)` is `true` for every unrecognised spelling, and *the
  // same for every ad* beside a path that resolves to nothing here would be a value reading as
  // proof (`#1002`). Reuses {@link knowableAt}'s question rather than restating it.
  if (knowableAt(builderKey, path) !== true) return undefined;
  return !fanOut.varies.includes(canonicalizeContextPath(path).split('.')[0]);
}

const ALIAS_BY_SPELLING = new Map(CONTEXT_ROOT_ALIASES.map((a) => [a.spelling, a]));
const ROOT_NAMES: ReadonlySet<string> = new Set(CONTEXT_ROOTS.map((r) => r.name));

/** Is `name` a root an author may address? */
export function isContextRoot(name: string): boolean {
  return ROOT_NAMES.has(name);
}

const OPEN_ROOTS: ReadonlySet<string> = new Set(CONTEXT_ROOTS.filter((r) => r.open).map((r) => r.name));

/**
 * Does this root hold arbitrary keys, so that any path beneath it is valid?
 *
 * Callers use it instead of enumerating: a schema walk that returns *unknown* for `params.pubid`
 * is reporting its own ignorance as the operator's mistake.
 */
export function isOpenContextRoot(name: string): boolean {
  return OPEN_ROOTS.has(name);
}

/**
 * Rewrite an alias spelling to the path it means. Everything else is returned unchanged.
 *
 * **Call this wherever a path enters** — a picker selection, typing, and above all a **paste**,
 * which is the route that produced the affirmed-but-dead paths this file exists to end. Returning
 * the input unchanged is the correct answer for a bare path: `resolveMacroPath` reads an unknown
 * first segment against the pod's flattened fields, which is a supported shape (`#1000`).
 */
export function canonicalizeContextPath(path: string): string {
  if (!path) return path;
  const dot = path.indexOf('.');
  const head = dot === -1 ? path : path.slice(0, dot);
  const alias = ALIAS_BY_SPELLING.get(head);
  if (!alias) return path;
  return dot === -1 ? alias.canonical : `${alias.canonical}${path.slice(dot)}`;
}
